import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Trash2, Syringe, FileCheck, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect, useMemo } from "react";

interface VaccinationData {
  vaccineName?: string;
  brandName?: string;
  dose?: string;
  volume?: string;
  route?: string;
  site?: string;
  manufacturer?: string;
  batchNumber?: string;
  expiryDate?: string;
  nextDueDate?: string;
  note?: string;
  [key: string]: any;
}

interface VaccinationItem {
  id?: string;
  sender?: string;
  message?: string;
  actionId?: number;
  details?: {
    payload?: string;
    actionName?: string;
    actionNumber?: string;
    isPerformed?: boolean;
    image?: string;
    [key: string]: any;
  } | null;
  isDraft?: boolean;
  displayTimestamp?: string;
  recordedAt?: string;
  [key: string]: any;
}

interface VaccineEntry {
  id: string;
  originalId: string;
  data: VaccinationData;
  timestamp: string;
  displayIndex: number; // To maintain order when new items are added
}

// Helper function to parse vaccination data
const parseVaccinationData = (item: VaccinationItem): VaccinationData[] => {
  try {
    if (item.details?.payload && typeof item.details.payload === "string") {
      const parsed = JSON.parse(item.details.payload);
      const vaccinations = parsed.vaccinationDetails?.vaccinations || [];
      return vaccinations;
    }
  } catch (e) {
    console.warn("Failed to parse vaccination payload", e);
  }
  return [];
};

export const VaccinationsActionItems = ({
  items,
  isDarkMode,
  handleVaccinationClick,
  handleGenerateCertificate,
  generatedVaccinationCertificate,
}) => {
  // Filter out items with no details and sort by timestamp
  const [vaccineEntries, setVaccineEntries] = useState<VaccineEntry[]>([]);
  const validItems = useMemo(
    () => items.filter((item) => item.details !== null),
    [items],
  );

  // Sort items by timestamp (oldest first)
  const sortedItems = useMemo(
    () =>
      [...validItems].sort((a: VaccinationItem, b: VaccinationItem) => {
        const dateA = new Date(
          a.displayTimestamp || a.recordedAt || 0,
        ).getTime();
        const dateB = new Date(
          b.displayTimestamp || b.recordedAt || 0,
        ).getTime();
        return dateA - dateB;
      }),
    [validItems],
  );

  // Update vaccine entries when sortedItems changes
  useEffect(() => {
    let displayCounter = 0;
    const entries: VaccineEntry[] = [];

    sortedItems.forEach((item) => {
      const vaccinations = parseVaccinationData(item);
      vaccinations.forEach((vaccine: VaccinationData) => {
        entries.push({
          id: `${item.id}-${Date.now()}-${Math.random()}`,
          originalId: item.id,
          data: vaccine,
          timestamp:
            item.displayTimestamp ||
            item.recordedAt ||
            new Date().toISOString(),
          displayIndex: displayCounter++,
        });
      });
    });

    setVaccineEntries(entries);
  }, [sortedItems]);

  return (
    <div className="px-3 pb-3 mt-3">
      <div>
        <div className="mt-3 space-y-2">
          {/* Map through merged vaccine entries */}
          {vaccineEntries.map((entry, index) => {
            const vaccineData = entry.data;
            const vaccineName =
              vaccineData?.vaccineName ||
              vaccineData?.brandName ||
              "Unknown Vaccine";
            const dose =
              vaccineData?.dose || vaccineData?.volume || "Standard dose";

            return (
              <div
                key={entry.id}
                className="flex items-start gap-2 p-2"
                onClick={() => handleVaccinationClick(vaccineData)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-mono text-gray-600`}>
                      #{index + 1}
                    </span>
                    <span
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {vaccineName}
                    </span>
                  </div>
                  <p
                    className={`text-xs ml-6 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Dose: {dose}
                    {vaccineData?.route && ` • Route: ${vaccineData?.route}`}
                    {vaccineData?.site && ` • Site: ${vaccineData?.site}`}
                  </p>
                  {vaccineData?.batchNumber && (
                    <p
                      className={`text-xs ml-6 ${
                        isDarkMode ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      Batch: {vaccineData?.batchNumber}
                      {vaccineData?.manufacturer &&
                        ` • ${vaccineData?.manufacturer}`}
                    </p>
                  )}
                </div>

                {/* Delete button with AlertDialog */}
                <div
                  className="flex items-center gap-1 transition-opacity duration-200 opacity-0 group-hover:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div onClick={(e) => e.stopPropagation()}>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        {/* <button
                          className={`p-1 rounded transition-colors ${
                            isDarkMode
                              ? "hover:bg-red-900/20 text-red-400 hover:text-red-300"
                              : "hover:bg-red-50 text-red-600 hover:text-red-700"
                          }`}
                          title="Delete vaccine"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button> */}
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Vaccine</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this vaccine? This
                            cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => {
                              // Remove this entry from the list
                              setVaccineEntries((prev) =>
                                prev.filter((e) => e.id !== entry.id),
                              );
                            }}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Certificate Banner */}
        {generatedVaccinationCertificate && (
          <div className="pt-3 mt-3 border-t border-gray-200">
            <div
              className="p-3 transition-all border border-gray-200 rounded-lg cursor-pointer bg-gray-50 hover:border-gray-300 hover:shadow-sm"
              onClick={handleGenerateCertificate}
            >
              <div className="flex items-center gap-2">
                <FileCheck
                  className={`w-4 h-4 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                />

                <div className="flex-1">
                  <p
                    className={`text-sm font-medium ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Vaccination Certificate Generated
                  </p>
                  <p
                    className={`text-xs ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {generatedVaccinationCertificate.vaccines?.length || 0}{" "}
                    vaccine(s) • Generated at{" "}
                    {new Date(
                      generatedVaccinationCertificate.generatedAt,
                    ).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <ChevronDown
                  className={`w-4 h-4 -rotate-90 ${
                    isDarkMode ? "text-emerald-400" : "text-emerald-600"
                  }`}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
